// Pure helpers for the cross-entity breadcrumb trail carried in router
// `location.state.trail`. No React/router imports — kept testable in isolation.

const MAX_STORED = 8;
const DEFAULT_VISIBLE = 4;

/**
 * `/AdminScreen/debts/debt/:id/:tab` -> `/AdminScreen/debts/debt/:id`.
 * Lets the trail compare entities while ignoring which tab is open.
 */
export function entityKeyFromPath(path) {
	if (typeof path !== 'string') {
		return '';
	}
	const [pathname] = path.split('?');
	const segments = pathname.split('/').filter(Boolean);
	// Detail routes are .../<domain>/<singular>/:id[/:tab] — keep through the id.
	return `/${segments.slice(0, 4).join('/')}`;
}

function isValidCrumb(crumb) {
	return (
		crumb &&
		typeof crumb === 'object' &&
		typeof crumb.label === 'string' &&
		crumb.label.length > 0 &&
		typeof crumb.to === 'string' &&
		crumb.to.length > 0
	);
}

/**
 * Validates untrusted `location.state.trail` — the history state is user
 * editable, so nothing here is trusted without a shape check.
 */
export function normalizeTrail(raw) {
	if (!Array.isArray(raw)) {
		return [];
	}
	return raw.filter(isValidCrumb).map((crumb) => ({ label: crumb.label, to: crumb.to }));
}

function truncateAtKey(trail, key) {
	if (!key) {
		return trail;
	}
	const index = trail.findIndex((crumb) => entityKeyFromPath(crumb.to) === key);
	return index === -1 ? trail : trail.slice(0, index);
}

/**
 * Builds the trail to hand off when navigating away from the page that owns
 * `origin` (its own `{root, current}` identity) to `destinationTo`.
 *
 * 1. Append `origin.current` onto the incoming trail — or seed `[root,
 *    current]` if the trail is empty — truncating first if `current` already
 *    appears in the trail. Cuts A -> B -> A -> B cycles from the *departing*
 *    side (the revisited entity was the one leaving).
 * 2. Truncate again if `destinationTo`'s own identity already appears in the
 *    result. Cuts the same cycle from the *arriving* side — without this, a
 *    Cuenta -> Deuda -> the SAME Cuenta hop would render the Cuenta twice
 *    (once as a trail link, once as that page's own `current`), since step 1
 *    only ever checks the page you're leaving, never the one you're headed
 *    to.
 * Capped to MAX_STORED to bound the history state size.
 */
export function appendCrumb(trail, { root, current }, destinationTo) {
	const safeTrail = normalizeTrail(trail);
	if (!current) {
		return safeTrail;
	}

	let next;
	if (safeTrail.length === 0) {
		next = [root, current].filter(isValidCrumb);
	} else {
		const currentKey = entityKeyFromPath(current.to);
		const existingIndex = safeTrail.findIndex((crumb) => entityKeyFromPath(crumb.to) === currentKey);
		next = existingIndex === -1 ? [...safeTrail, current] : [...safeTrail.slice(0, existingIndex), current];
	}

	next = next.slice(-MAX_STORED);
	return truncateAtKey(next, entityKeyFromPath(destinationTo));
}

/**
 * Collapses a trail for rendering: keeps the first and last `max - 1`
 * entries, replacing hidden middle entries with a single ellipsis marker.
 * Each real entry carries its original index into `trail`, so a caller can
 * navigate "back" to it via `trail.slice(0, entry.index)` without losing
 * track of collapsed positions.
 */
export function collapseTrail(trail, max = DEFAULT_VISIBLE) {
	const safeTrail = normalizeTrail(trail);
	if (safeTrail.length <= max) {
		return safeTrail.map((crumb, index) => ({ ...crumb, index }));
	}

	const tailCount = max - 1;
	const head = { ...safeTrail[0], index: 0 };
	const tailStart = safeTrail.length - tailCount;
	const tail = safeTrail.slice(tailStart).map((crumb, i) => ({ ...crumb, index: tailStart + i }));
	return [head, { ellipsis: true }, ...tail];
}
